import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Dashboard from './pages/Dashboard';
import ManageDrivers from './pages/ManageDrivers';
import ManageVehicles from './pages/ManageVehicles';
import Profile from './pages/Profile';
import Reports from './pages/Reports';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyOTPReset from './pages/VerifyOTPReset';
import TermsAndConditions from './pages/TermsAndConditions';

function AppContent() {
  const { isLoading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jd-blue mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Layout component for authenticated routes
  const AuthenticatedLayout = ({ children }: { children: React.ReactNode }) => (
    <ProtectedRoute>
      <div className="flex h-screen bg-gray-100">
        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        
        {/* Sidebar */}
        <Sidebar 
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />
        
        {/* Main content */}
        <div className="flex-1 flex flex-col bg-white min-w-0">
          <Header onMenuClick={() => setSidebarOpen(true)} />
          <main className="flex-1 overflow-auto bg-gray-100">
            {children}
          </main>
        </div>
      </div>
    </ProtectedRoute>
  );

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-otp-reset" element={<VerifyOTPReset />} />
      <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
      
      {/* Protected routes */}
      <Route path="/" element={
        <AuthenticatedLayout>
          <Dashboard />
        </AuthenticatedLayout>
      } />
      
      <Route path="/dashboard" element={
        <AuthenticatedLayout>
          <Dashboard />
        </AuthenticatedLayout>
      } />
      
      <Route path="/drivers" element={
        <AuthenticatedLayout>
          <ManageDrivers />
        </AuthenticatedLayout>
      } />
      
      <Route path="/vehicles" element={
        <AuthenticatedLayout>
          <ManageVehicles />
        </AuthenticatedLayout>
      } />
      
      <Route path="/reports" element={
        <AuthenticatedLayout>
          <Reports />
        </AuthenticatedLayout>
      } />
      
      <Route path="/profile" element={
        <AuthenticatedLayout>
          <Profile />
        </AuthenticatedLayout>
      } />
      
      {/* Catch all route - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
